import {TimeTrackingImportColumnMapping} from '../../../features/time-tracking-import/types';
import {distance} from 'fastest-levenshtein';
import {EMPTY_MAPPING, HEADER_ALIASES, SUPPORTED_EXTENSIONS} from '../constants';

interface ZipEntry {
    fileName: string;
    compressionMethod: number;
    compressedSize: number;
    localHeaderOffset: number;
}

type DecompressionStreamLike = ReadableWritablePair<Uint8Array, Uint8Array>;
type DecompressionStreamConstructor = new (format: 'deflate-raw') => DecompressionStreamLike;
type ReadableStreamWithPipeThrough<TChunk> = ReadableStream<TChunk> & {
    pipeThrough<TResult>(
        transform: ReadableWritablePair<TChunk, TResult>
    ): ReadableStream<TResult>;
};

const ZIP_END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06054b50;
const ZIP_CENTRAL_DIRECTORY_SIGNATURE = 0x02014b50;
const ZIP_LOCAL_FILE_SIGNATURE = 0x04034b50;

const normalizeHeader = (value: string): string =>
    value.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');

const compactHeader = (value: string): string =>
    normalizeHeader(value).replace(/[^a-z0-9]+/g, '');

const getHeaderMatchDistance = (header: string, alias: string): number => {
    const normalizedHeader = normalizeHeader(header);
    const normalizedAlias = normalizeHeader(alias);
    const headerCompact = compactHeader(header);
    const aliasCompact = compactHeader(alias);
    const tokenVariants: Array<[string, string]> = normalizedHeader
        .split(' ')
        .flatMap((token): Array<[string, string]> => [
            [token, normalizedAlias],
            [compactHeader(token), aliasCompact],
        ]);
    const baseVariants: Array<[string, string]> = [
        [normalizedHeader, normalizedAlias],
        [headerCompact, aliasCompact],
        ...tokenVariants,
    ];
    const variants = baseVariants.filter(([left, right]) => left.length > 0 && right.length > 0);

    return variants.reduce((bestDistance, [left, right]) => {
        const value = distance(left, right) / Math.max(left.length, right.length);
        return Math.min(bestDistance, value);
    }, Number.POSITIVE_INFINITY);
};

const canUseFuzzyHeaderMatch = (normalizedDistance: number): boolean =>
    normalizedDistance <= 0.28;

const getUniqueHeaders = (headers: string[]): string[] =>
    headers.filter((column, index, list) => column.length > 0 && list.indexOf(column) === index);

export const getFileExtension = (fileName: string): string => {
    const fileNameParts = fileName.split('.');
    return fileNameParts.length > 1 ? fileNameParts[fileNameParts.length - 1]!.toLowerCase() : '';
};

export const isSupportedFile = (file: File): boolean =>
    SUPPORTED_EXTENSIONS.includes(getFileExtension(file.name));

function splitCsvLine(line: string, delimiter: string): string[] {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let index = 0; index < line.length; index += 1) {
        const char = line[index];
        const nextChar = line[index + 1];

        if (char === '"') {
            if (inQuotes && nextChar === '"') {
                current += '"';
                index += 1;
            } else {
                inQuotes = !inQuotes;
            }
            continue;
        }

        if (!inQuotes && char === delimiter) {
            values.push(current.trim());
            current = '';
            continue;
        }

        current += char;
    }

    values.push(current.trim());
    return values.map((value) => value.replace(/^\uFEFF/, '').trim());
}

const detectDelimiter = (line: string): string => {
    const variants = [',', ';', '\t'];
    let resolvedDelimiter = ',';
    let maxColumns = 0;

    variants.forEach((candidate) => {
        const columns = splitCsvLine(line, candidate).length;
        if (columns > maxColumns) {
            maxColumns = columns;
            resolvedDelimiter = candidate;
        }
    });

    return resolvedDelimiter;
};

const extractCsvHeaders = async (file: File): Promise<string[]> => {
    const content = await file.text();
    const headerLine = content
        .split(/\r?\n/)
        .map((line) => line.trim())
        .find((line) => line.length > 0);

    if (!headerLine) {
        return [];
    }

    return getUniqueHeaders(
        splitCsvLine(headerLine, detectDelimiter(headerLine)).map((column) => column.trim())
    );
};

const normalizePath = (path: string): string => {
    const segments = path.split('/').filter((segment) => segment.length > 0 && segment !== '.');
    const normalized: string[] = [];

    segments.forEach((segment) => {
        if (segment === '..') {
            normalized.pop();
            return;
        }

        normalized.push(segment);
    });

    return normalized.join('/');
};

const toAbsoluteSheetPath = (target: string): string => {
    const normalizedTarget = target.replace(/^\/+/, '');
    if (normalizedTarget.startsWith('xl/')) {
        return normalizePath(normalizedTarget);
    }

    return normalizePath(`xl/${normalizedTarget}`);
};

const parseCellReference = (value: string | null): number => {
    if (!value) {
        return Number.MAX_SAFE_INTEGER;
    }

    const letters = (value.match(/^[A-Za-z]+/)?.[0] ?? '').toUpperCase();
    if (!letters) {
        return Number.MAX_SAFE_INTEGER;
    }

    let column = 0;
    for (let index = 0; index < letters.length; index += 1) {
        column = (column * 26) + (letters.charCodeAt(index) - 64);
    }

    return column;
};

const streamToUint8Array = async (stream: ReadableStream<Uint8Array>): Promise<Uint8Array> => {
    const reader = stream.getReader();
    const chunks: Uint8Array[] = [];
    let total = 0;
    let isDone = false;

    while (!isDone) {
        const {done, value} = await reader.read();
        if (done) {
            isDone = true;
            continue;
        }

        if (value) {
            chunks.push(value);
            total += value.length;
        }
    }

    const result = new Uint8Array(total);
    let offset = 0;
    chunks.forEach((chunk) => {
        result.set(chunk, offset);
        offset += chunk.length;
    });

    return result;
};

const inflateRaw = async (bytes: Uint8Array): Promise<Uint8Array> => {
    const DecompressionStreamCtor = (
        globalThis as {DecompressionStream?: DecompressionStreamConstructor}
    ).DecompressionStream;
    if (!DecompressionStreamCtor) {
        throw new Error('DecompressionStream is not available');
    }

    const sourceStream = new Blob([bytes]).stream() as unknown as ReadableStreamWithPipeThrough<Uint8Array>;
    const stream = sourceStream.pipeThrough(new DecompressionStreamCtor('deflate-raw'));
    return await streamToUint8Array(stream);
};

const readZipEntries = (archive: Uint8Array): ZipEntry[] => {
    const view = new DataView(archive.buffer, archive.byteOffset, archive.byteLength);
    const searchStart = Math.max(0, archive.length - 65557);

    let eocdOffset = -1;
    for (let offset = archive.length - 22; offset >= searchStart; offset -= 1) {
        if (view.getUint32(offset, true) === ZIP_END_OF_CENTRAL_DIRECTORY_SIGNATURE) {
            eocdOffset = offset;
            break;
        }
    }

    if (eocdOffset < 0) {
        throw new Error('Invalid xlsx archive: central directory was not found');
    }

    const entriesCount = view.getUint16(eocdOffset + 10, true);
    const centralDirectoryOffset = view.getUint32(eocdOffset + 16, true);

    const decoder = new TextDecoder('utf-8');
    const entries: ZipEntry[] = [];
    let cursor = centralDirectoryOffset;

    for (let index = 0; index < entriesCount; index += 1) {
        if (view.getUint32(cursor, true) !== ZIP_CENTRAL_DIRECTORY_SIGNATURE) {
            throw new Error('Invalid xlsx archive: malformed central directory');
        }

        const compressionMethod = view.getUint16(cursor + 10, true);
        const compressedSize = view.getUint32(cursor + 20, true);
        const fileNameLength = view.getUint16(cursor + 28, true);
        const extraLength = view.getUint16(cursor + 30, true);
        const commentLength = view.getUint16(cursor + 32, true);
        const localHeaderOffset = view.getUint32(cursor + 42, true);

        const fileNameStart = cursor + 46;
        const fileNameEnd = fileNameStart + fileNameLength;
        const fileName = decoder.decode(archive.slice(fileNameStart, fileNameEnd));

        entries.push({
            fileName,
            compressionMethod,
            compressedSize,
            localHeaderOffset,
        });

        cursor = fileNameEnd + extraLength + commentLength;
    }

    return entries;
};

const readZipEntryText = async (archive: Uint8Array, entry: ZipEntry): Promise<string> => {
    const view = new DataView(archive.buffer, archive.byteOffset, archive.byteLength);
    const localOffset = entry.localHeaderOffset;

    if (view.getUint32(localOffset, true) !== ZIP_LOCAL_FILE_SIGNATURE) {
        throw new Error(`Invalid xlsx archive: bad local header for ${entry.fileName}`);
    }

    const fileNameLength = view.getUint16(localOffset + 26, true);
    const extraLength = view.getUint16(localOffset + 28, true);
    const dataStart = localOffset + 30 + fileNameLength + extraLength;
    const compressedData = archive.slice(dataStart, dataStart + entry.compressedSize);

    let data = compressedData;
    if (entry.compressionMethod === 8) {
        data = await inflateRaw(compressedData);
    } else if (entry.compressionMethod !== 0) {
        throw new Error(`Unsupported xlsx compression method ${entry.compressionMethod}`);
    }

    return new TextDecoder('utf-8').decode(data);
};

const parseWorkbookSheetPath = (workbookXml: string, relationshipsXml: string): string => {
    const parser = new DOMParser();
    const workbookDoc = parser.parseFromString(workbookXml, 'application/xml');
    const relsDoc = parser.parseFromString(relationshipsXml, 'application/xml');

    const firstSheet = workbookDoc.getElementsByTagName('sheet').item(0);
    if (!firstSheet) {
        throw new Error('Workbook does not contain sheets');
    }

    const relationshipId = firstSheet.getAttribute('r:id') ?? firstSheet.getAttributeNS(
        'http://schemas.openxmlformats.org/officeDocument/2006/relationships',
        'id'
    );

    if (!relationshipId) {
        throw new Error('Workbook sheet relationship is missing');
    }

    const relationships = relsDoc.getElementsByTagName('Relationship');
    for (let index = 0; index < relationships.length; index += 1) {
        const relationship = relationships.item(index);
        if (relationship?.getAttribute('Id') === relationshipId) {
            const target = relationship.getAttribute('Target');
            if (target) {
                return toAbsoluteSheetPath(target);
            }
        }
    }

    throw new Error('Workbook sheet target was not found');
};

const parseSharedStrings = (sharedStringsXml: string): string[] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(sharedStringsXml, 'application/xml');
    const siNodes = Array.from(doc.getElementsByTagName('si'));

    return siNodes.map((siNode) => {
        const textNodes = Array.from(siNode.getElementsByTagName('t'));
        return textNodes.map((node) => node.textContent ?? '').join('');
    });
};

const parseSheetHeaderRow = (sheetXml: string, sharedStrings: string[]): string[] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(sheetXml, 'application/xml');
    const firstRow = doc.getElementsByTagName('row').item(0);
    if (!firstRow) {
        return [];
    }

    const cells = Array.from(firstRow.getElementsByTagName('c'))
        .map((cellNode) => {
            const type = cellNode.getAttribute('t');
            const cellRef = cellNode.getAttribute('r');
            let value = '';

            if (type === 'inlineStr') {
                value = Array.from(cellNode.getElementsByTagName('t'))
                    .map((node) => node.textContent ?? '')
                    .join('');
            } else {
                const valueNode = cellNode.getElementsByTagName('v').item(0);
                const raw = valueNode?.textContent ?? '';
                if (type === 's') {
                    const index = Number(raw);
                    value = Number.isFinite(index) ? (sharedStrings[index] ?? '') : '';
                } else {
                    value = raw;
                }
            }

            return {
                column: parseCellReference(cellRef),
                value: value.trim(),
            };
        })
        .filter((cell) => cell.value.length > 0)
        .sort((a, b) => a.column - b.column);

    return cells.map((cell) => cell.value);
};

const extractXlsxHeaders = async (file: File): Promise<string[]> => {
    const archive = new Uint8Array(await file.arrayBuffer());
    const entries = readZipEntries(archive);
    const entryMap = new Map(entries.map((entry) => [entry.fileName.replace(/\\/g, '/'), entry]));

    const workbookEntry = entryMap.get('xl/workbook.xml');
    const relationshipsEntry = entryMap.get('xl/_rels/workbook.xml.rels');
    if (!workbookEntry || !relationshipsEntry) {
        throw new Error('Workbook structure is incomplete');
    }

    const workbookXml = await readZipEntryText(archive, workbookEntry);
    const relationshipsXml = await readZipEntryText(archive, relationshipsEntry);
    const sheetPath = parseWorkbookSheetPath(workbookXml, relationshipsXml);

    const sheetEntry = entryMap.get(sheetPath);
    if (!sheetEntry) {
        throw new Error('First worksheet was not found');
    }

    const sharedStringsEntry = entryMap.get('xl/sharedStrings.xml');
    const sharedStrings = sharedStringsEntry
        ? parseSharedStrings(await readZipEntryText(archive, sharedStringsEntry))
        : [];

    return getUniqueHeaders(parseSheetHeaderRow(await readZipEntryText(archive, sheetEntry), sharedStrings));
};

export const extractImportFileHeaders = async (file: File): Promise<string[]> => {
    const extension = getFileExtension(file.name);

    if (!SUPPORTED_EXTENSIONS.includes(extension)) {
        throw new Error(`Unsupported file extension: ${extension}`);
    }

    return extension === 'csv'
        ? await extractCsvHeaders(file)
        : await extractXlsxHeaders(file);
};

export const createInitialMapping = (headers: string[]): TimeTrackingImportColumnMapping => {
    if (headers.length < 1) {
        return {...EMPTY_MAPPING};
    }

    const findHeader = (key: keyof TimeTrackingImportColumnMapping): string => {
        const aliases = HEADER_ALIASES[key];
        const exactMatch = headers.find((header) => aliases.includes(normalizeHeader(header)));

        if (exactMatch) {
            return exactMatch;
        }

        const fuzzyMatch = headers
            .map((header) => ({
                header,
                distance: Math.min(...aliases.map((alias) => getHeaderMatchDistance(header, alias))),
            }))
            .filter((match) => canUseFuzzyHeaderMatch(match.distance))
            .sort((left, right) => left.distance - right.distance)[0];

        return fuzzyMatch?.header ?? '';
    };

    return {
        employeeColumn: findHeader('employeeColumn'),
        employeeName: '',
        dateColumn: findHeader('dateColumn'),
        hoursColumn: findHeader('hoursColumn'),
        projectOrAbsenceColumn: findHeader('projectOrAbsenceColumn'),
        typeColumn: findHeader('typeColumn'),
        noteColumn: findHeader('noteColumn'),
        tagsColumn: findHeader('tagsColumn'),
    };
};
